import "./update.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useEffect, useState, useContext } from "react";
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  getDocs,
  where,
  query,
  orderBy,
  updateDoc
} from "firebase/firestore";
import { db, storage } from "../../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { useSidebarState } from "../../components/sidebar/SidebarStateContext";
import { updateServiceUsersArray } from '../new/utils/FirebaseUtils';
import { ImageUploader } from '../new/utils/NewUtils';
import EmpresaComponent from '../new/utils/EmpresaComponent';
import { uploadErrorLogToFirestore } from "../../utils/logUtils";
import { fetchClientesPorAdministrativo, fetchClientesPorInspector } from "../../components/admin/firestoreService";
import { createFirebaseAuthUser, generateUniqueEmail, verifyAuthRegistration } from "../../utils/AuthUtils";
import ClienteSearch from '../../utils/ClienteSearch'; 
import ServicioSearch from '../../utils/ServicioSearch'; 
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import Button from '@mui/material/Button';

/**
 * Componente para la actualización de un usuario de seguridad.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Array} props.inputs - Datos de entrada para el formulario.
 * @param {string} props.title - Título del componente.
 * @returns {JSX.Element} - Componente Update.
 */
const Update = ({ inputs, title }) => {

  const [file, setFile] = useState("");
  const [data, setData] = useState({ tiendas: [] });
  const [per, setPerc] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [tiendasCliente, setTiendasCliente] = useState([]);
  const [tiendas, setTiendas] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState(data.tiendas.map(tienda => tienda.numero.toString()));
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [isAllClientesSelected, setIsAllClientesSelected] = useState(false);
  const [isAllTiendasSelected, setIsAllTiendasSelected] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [selectedEmpresa, setSelectedEmpresa] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  
  // Estados para el diálogo de registro en sistema de autenticación
  const [openAuthDialog, setOpenAuthDialog] = useState(false);
  const [authPassword, setAuthPassword] = useState("");
  const [isRegisteringAuth, setIsRegisteringAuth] = useState(false);
  
  // Estados para la verificación mejorada de Auth
  const [needsAuth, setNeedsAuth] = useState(false);
  const [authStatus, setAuthStatus] = useState('unknown');
  const [authMessage, setAuthMessage] = useState('');
  
  // Estados para la búsqueda en los componentes
  const [clienteSearchTerm, setClienteSearchTerm] = useState('');
  const [servicioSearchTerm, setServicioSearchTerm] = useState('');
  
  // Estado para almacenar el email original del vigilante
  const [originalEmail, setOriginalEmail] = useState('');

  const navigate = useNavigate();
  const { currentUser, currentService, adminData } = useContext(AuthContext);
  const { collapsed } = useSidebarState();
  const { id } = useParams();

  // Agregar una clase basada en el estado de colapso
  const containerClass = collapsed ? "newContainer collapsed" : "newContainer";

  // Manejador para el botón volver
  const handleVolver = () => {
    // Navegar explícitamente a la ruta de usuarios
    navigate('/vigilantes');
  };

  // Cargar clientes y tiendas del servicio actual (misma lógica que New)
  useEffect(() => {
    const fetchData = async () => {
      if (!currentService?.id || !adminData) return;
      try {
        setIsLoading(true);
        const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
        const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');

        // Clientes: como en New — adminData.clientes (IDs) o todos con orderBy('nombre')
        let clientesData = [];
        if (adminData.clientes && Array.isArray(adminData.clientes) && adminData.clientes.length > 0) {
          const BATCH_SIZE = 10;
          for (let i = 0; i < adminData.clientes.length; i += BATCH_SIZE) {
            const batch = adminData.clientes.slice(i, i + BATCH_SIZE);
            const queries = batch.map(clienteId =>
              query(clientesRef, where('__name__', '==', clienteId))
            );
            const snapshots = await Promise.all(queries.map(q => getDocs(q)));
            const batchDocs = snapshots.flatMap(s => s.docs);
            const batchClientes = batchDocs.map(d => ({ id: d.id, ...d.data() }));
            clientesData = [...clientesData, ...batchClientes];
          }
        } else {
          // Solo clientes asignados al usuario (por rol): carga más ligera
          if (adminData.tipoUsuario?.includes('administrativo')) {
            clientesData = await fetchClientesPorAdministrativo(adminData.id, currentService);
          } else if (adminData.tipoUsuario?.includes('inspector')) {
            clientesData = await fetchClientesPorInspector(adminData.id, currentService);
          } else {
            clientesData = [];
          }
          clientesData = (clientesData || []).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        }

        // Tiendas: todas del servicio actual (como loadServicios en New)
        const tiendasSnapshot = await getDocs(tiendasRef);
        const allTiendas = tiendasSnapshot.docs.map(d => {
          const data = d.data();
          const numTienda = data.numTienda ?? data.localizacion ?? d.id;
          return { id: d.id, ...data, numTienda };
        });
        allTiendas.sort((a, b) => (a.numTienda - b.numTienda) || 0);

        setClientes(clientesData);
        setTiendas(allTiendas);
      } catch (err) {
        console.error('Error cargando clientes/tiendas:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser && currentService && adminData) {
      fetchData();
    }
  }, [currentUser, currentService?.id, adminData?.id]);

  useEffect(() => {
    const fetchUsuarioData = async () => {
      try {
        setIsLoadingUserData(true);
        // Usar currentService directamente en lugar de hacer otra consulta
        if (!currentService?.id) {
          console.error("No hay servicio actual disponible");
          return;
        }
        
        // Obtener directamente el documento del vigilante usando currentService
        const vigilanteDocRef = doc(db, "servicios", currentService.id, "vigilantes", id);
        const vigilanteDoc = await getDoc(vigilanteDocRef);

        if (vigilanteDoc.exists()) {
          const vigilanteData = vigilanteDoc.data();
          setData(vigilanteData);
          
          // Almacenar el email original para comparar cambios
          setOriginalEmail(vigilanteData.email || '');

          // Verificar el estado de autenticación usando la nueva función
          const authCheck = verifyAuthRegistration(vigilanteData);
          setNeedsAuth(authCheck.needsAuth);
          setAuthStatus(authCheck.authStatus);
          setAuthMessage(authCheck.message || '');

          // Establecer las tiendas seleccionadas
          setSelectedOptions(vigilanteData.tiendas?.map(tienda => tienda.numero.toString()) || []);

          // Obtener y establecer los clientes de las tiendas (comparar numero como string)
          const clientesDelUsuario = new Set();
          vigilanteData.tiendas?.forEach(tienda => {
            const tiendaCompleta = tiendas.find(t =>
              String(t.numTienda) === String(tienda.numero)
            );
            if (tiendaCompleta) {
              const clientesArray = Array.isArray(tiendaCompleta.cliente)
                ? tiendaCompleta.cliente
                : tiendaCompleta.cliente
                  ? [tiendaCompleta.cliente]
                  : [];
              clientesArray.forEach(c => clientesDelUsuario.add(c));
            }
          });
          setSelectedClientes(Array.from(clientesDelUsuario));

          // Actualizar las tiendas disponibles para los clientes seleccionados
          const tiendasFiltradas = tiendas.filter(tienda => {
            // Asegurarse de que cliente sea siempre un array
            const clientesArray = Array.isArray(tienda.cliente)
              ? tienda.cliente
              : tienda.cliente
                ? [tienda.cliente]
                : [];

            return clientesArray.some(c => clientesDelUsuario.has(c));
          });
          setTiendasCliente(tiendasFiltradas);
          
          // Establecer la empresa seleccionada
          if (vigilanteData.empresaId) {
            setSelectedEmpresa(vigilanteData.empresaId);
          }
        }
      } catch (err) {
        console.error("Error fetching usuario data:", err);
      } finally {
        setIsLoadingUserData(false);
      }
    };

    // Ejecutar cuando tengamos los datos necesarios
    if (currentUser && id && currentService?.id && tiendas.length > 0) {
      fetchUsuarioData();
    }
  }, [currentUser, id, currentService?.id, tiendas]);

  useEffect(() => {
    const uploadFile = () => {
      const name = new Date().getTime() + file.name;

      const storageRef = ref(storage, file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("Upload is " + progress + "% done");
          setPerc(progress);
          switch (snapshot.state) {
            case "paused":
              console.log("Upload is paused");
              break;
            case "running":
              console.log("Upload is running");
              break;
            default:
              break;
          }
        },
        (error) => {
          uploadErrorLogToFirestore(error)
          console.log(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            setData((prev) => ({ ...prev, img: downloadURL }));
          });
        }
      );
    };
    file && uploadFile();
  }, [file]);

  /**
   * Maneja el cambio de los campos de entrada.
   */
  const handleInput = (e) => {
    const id = e.target.id;
    const value = id === 'email' ? e.target.value.toLowerCase() : e.target.value;

    setData({ ...data, [id]: value });
  };

  // Manejador para cuando cambia la empresa seleccionada
  const handleEmpresaChange = (empresaData) => {
    setData(prev => ({
      ...prev,
      empresaId: empresaData.empresaId,
      empresaNombre: empresaData.empresaNombre,
      codEmpresa: empresaData.codEmpresa
    }));
  };

  // Manejador de cambio de cliente seleccionado
  const handleClienteChange = (clientesSeleccionados) => {
    setSelectedClientes(clientesSeleccionados);
    setIsAllClientesSelected(clientesSeleccionados.length === clientes.length);

    // Filtrar tiendas basadas en los clientes seleccionados (cliente puede ser array o string)
    const filteredTiendas = tiendas.filter((tienda) => {
      const clientesDeTienda = Array.isArray(tienda?.cliente)
        ? tienda.cliente
        : tienda?.cliente
          ? [tienda.cliente]
          : [];
      return clientesSeleccionados.some((selectedCliente) =>
        clientesDeTienda.some((c) => String(c) === String(selectedCliente))
      );
    });

    // Actualizar la lista de tiendas basadas en los clientes seleccionados
    setTiendasCliente(filteredTiendas);

    // Mantener solo servicios que sigan disponibles para los clientes seleccionados
    const newSelectedOptions = selectedOptions.filter(option =>
      filteredTiendas.some(tienda => tienda.numTienda.toString() === option)
    );
    setSelectedOptions(newSelectedOptions);

    // Siempre sincronizar data.tiendas con la selección actual (visible)
    const selectedTiendas = filteredTiendas
      .filter(tienda => newSelectedOptions.includes(tienda.numTienda.toString()))
      .map(tienda => ({ numero: tienda.numTienda }));
    setData(prev => ({ ...prev, tiendas: selectedTiendas }));
  };

  // Manejador de cambio de servicios seleccionados
  const handleCheckboxChange = (selectedValues) => {
    setSelectedOptions(selectedValues);
    setIsAllTiendasSelected(selectedValues.length === tiendasCliente.length);

    // Convertir las tiendas seleccionadas al formato correcto
    const selectedTiendas = tiendasCliente
      .filter(tienda => selectedValues.includes(tienda.numTienda.toString()))
      .map(tienda => ({
        numero: tienda.numTienda
      }));

    setData(prev => ({ ...prev, tiendas: selectedTiendas }));
  };

  /**
   * Registra el usuario en el sistema de autenticación
   */
  const handleRegisterAuth = async () => {
    setIsRegisteringAuth(true);
    
    try {
      // Generar un email único si no hay uno
      let email = data.email;
      if (!email || email.trim() === '') {
        email = generateUniqueEmail(data.tipVS, currentService.id);
      }
      
      // Usar el DNI como contraseña, o la contraseña proporcionada en el diálogo
      const password = authPassword || data.dni;
      
      if (!password || password.length < 6) {
        throw new Error("La contraseña debe tener al menos 6 caracteres");
      }
      
      // Nombre completo para el perfil
      const displayName = `${data.nombre} ${data.apellidos}`;
      
      // Crear el usuario en el sistema de autenticación
      const result = await createFirebaseAuthUser(email, password, displayName);
      
      if (result.success) {
        // Actualizar el documento con el UID de Auth
        const vigilanteDocRef = doc(db, "servicios", currentService.id, "vigilantes", id);
        await updateDoc(vigilanteDocRef, {
          authUID: result.uid,
          email: email  // Guardar el email generado
        });
        
        setData(prev => ({
          ...prev,
          authUID: result.uid,
          email: email
        }));
        
        // Actualizar el estado de Auth
        setNeedsAuth(false);
        setAuthStatus('registered');
        
        setOpenAuthDialog(false);
        setShowSuccessMessage(true);
        setUpdateMessage("Usuario registrado correctamente en el sistema");
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error("Error al registrar en el sistema de autenticación:", err);
      window.alert("Error al registrar en el sistema: " + err.message);
    } finally {
      setIsRegisteringAuth(false);
    }
  };

  /**
   * Maneja el registro de la entrada actualizada.
   */
  const handleAdd = async (e) => {
    e.preventDefault();

    // Construir tiendas a guardar desde el estado actual de la UI (selectedOptions + tiendasCliente)
    const tiendasToSave = tiendasCliente
      .filter((t) => selectedOptions.includes(t.numTienda.toString()))
      .map((t) => ({ numero: t.numTienda }));

    // Validaciones de tiendas...
    const storeNumbers = tiendasToSave.map((tienda) => tienda.numero);
    const uniqueStoreNumbers = new Set(storeNumbers);
    if (storeNumbers.length !== uniqueStoreNumbers.size) {
      window.alert("Registro fallido: Hay tiendas duplicadas");
      return;
    }

    if (storeNumbers.length === 0) {
      window.alert("Registro fallido: No se ha agregado ninguna tienda");
      return;
    }

    const validStoreNumbers = tiendas.map((tienda) => tienda.numTienda);
    const selectedStoreNumbers = tiendasToSave.map((tienda) => tienda.numero);
    const isValidStore = selectedStoreNumbers.every((number) =>
      validStoreNumbers.includes(number)
    );
    if (!isValidStore) {
      window.alert(
        "Registro fallido: El identificador de servicio seleccionado no es válido"
      );
      return;
    }

    // Verificar que se ha seleccionado una empresa
    if (!data.empresaId) {
      window.alert("Registro fallido: Debe seleccionar una empresa");
      return;
    }

    try {
      if (!currentUser?.email || !currentService?.id || !id) {
        console.error("Faltan datos necesarios para la actualización");
        window.alert("Error: Faltan datos necesarios para la actualización. Compruebe que está logueado correctamente y que hay un servicio seleccionado.");
        return;
      }

      setIsUpdating(true);

      // Información de diagnóstico
      console.log("Intentando actualizar usuario con los siguientes datos:", {
        usuarioActual: currentUser.email,
        servicioId: currentService.id,
        usuarioId: id,
        rutaDocumento: `servicios/${currentService.id}/vigilantes/${id}`
      });

      // Verificar primero si el documento existe
      const vigilanteDocRef = doc(db, "servicios", currentService.id, "vigilantes", id);
      const docSnap = await getDoc(vigilanteDocRef);
      
      if (!docSnap.exists()) {
        throw new Error(`No se encontró el documento en la ruta: servicios/${currentService.id}/vigilantes/${id}`);
      }

      // Preparar datos para actualización (usar tiendas construidas desde la UI)
      const updateData = {
        ...data,
        tiendas: tiendasToSave,
        borrado: false,
        timeStamp: serverTimestamp(),
      };

      // Si persona está vacío, eliminarlo del objeto
      if (!updateData.persona) {
        delete updateData.persona;
      }

      // Simplificar el objeto de actualización para reducir problemas
      const datosBasicos = {
        nombre: updateData.nombre || '',
        apellidos: updateData.apellidos || '',
        email: updateData.email || '',
        tiendas: updateData.tiendas || [],
        empresaId: updateData.empresaId,
        empresaNombre: updateData.empresaNombre,
        codEmpresa: updateData.codEmpresa,
        timeStamp: updateData.timeStamp,
        borrado: updateData.borrado
      };
      
      if (updateData.dni) datosBasicos.dni = updateData.dni;
      if (updateData.tipVS) datosBasicos.tipVS = updateData.tipVS;
      if (updateData.telefono) datosBasicos.telefono = updateData.telefono;
      if (updateData.img) datosBasicos.img = updateData.img;
      if (updateData.persona) datosBasicos.persona = updateData.persona;

      console.log("Datos de actualización preparados:", datosBasicos);

      // Dividir la actualización en partes más pequeñas para evitar problemas
      try {
        // Parte 1: Campos básicos
        await updateDoc(vigilanteDocRef, {
          nombre: datosBasicos.nombre,
          apellidos: datosBasicos.apellidos,
          email: datosBasicos.email,
          borrado: datosBasicos.borrado,
          timeStamp: datosBasicos.timeStamp
        });
        
        // Parte 2: Empresa
        await updateDoc(vigilanteDocRef, {
          empresaId: datosBasicos.empresaId,
          empresaNombre: datosBasicos.empresaNombre,
          codEmpresa: datosBasicos.codEmpresa
        });
        
        // Parte 3: Campos opcionales
        const camposOpcionales = {};
        if (datosBasicos.dni) camposOpcionales.dni = datosBasicos.dni;
        if (datosBasicos.tipVS) camposOpcionales.tipVS = datosBasicos.tipVS;
        if (datosBasicos.telefono) camposOpcionales.telefono = datosBasicos.telefono;
        if (datosBasicos.img) camposOpcionales.img = datosBasicos.img;
        if (datosBasicos.persona) camposOpcionales.persona = datosBasicos.persona;
        
        if (Object.keys(camposOpcionales).length > 0) {
          await updateDoc(vigilanteDocRef, camposOpcionales);
        }
        
        // Parte 4: Tiendas (puede ser más complejo)
        await updateDoc(vigilanteDocRef, {
          tiendas: datosBasicos.tiendas
        });
        
        // Parte 5: Actualizar array de usuarios del servicio si el email cambió
        if (originalEmail !== datosBasicos.email) {
          console.log("Email cambió, actualizando array de usuarios del servicio:", {
            originalEmail,
            newEmail: datosBasicos.email
          });
          
          const updateUsersResult = await updateServiceUsersArray(
            currentService.id,
            originalEmail,
            datosBasicos.email
          );
          
          if (!updateUsersResult.success) {
            console.error("Error actualizando array de usuarios del servicio:", updateUsersResult.error);
            // No lanzar error aquí para no interrumpir la actualización del vigilante
          }
        }
        
        console.log("Usuario actualizado correctamente");
        
        // Si el usuario necesita ser registrado en Auth y tiene un DNI válido,
        // mostrar el diálogo para confirmar
        if (needsAuth && data.dni && data.dni.length >= 6) {
          setOpenAuthDialog(true);
        } else {
          setUpdateMessage("Usuario actualizado correctamente");
          setShowSuccessMessage(true);
          
          // Esperar un momento para mostrar el mensaje de éxito
          setTimeout(() => {
            navigate(-1);
          }, 1500);
        }
      } catch (updateError) {
        console.error("Error específico en updateDoc:", updateError);
        
        // Intentar determinar el tipo de error de permisos
        if (updateError.code === 'permission-denied') {
          window.alert("Error de permisos: No tienes permisos suficientes para actualizar este documento. Verifica que tu sesión sea válida y que tengas los permisos adecuados.");
        } else {
          throw updateError; // Relanzar para el catch externo
        }
      }

    } catch (err) {
      console.error("Error en la actualización:", err);
      
      // Evitar intentar registrar el error para no crear más errores
      /*
      try {
        uploadErrorLogToFirestore("error actualizando usuario: " + err.message);
      } catch (logError) {
        console.error("Error adicional al intentar registrar el error:", logError);
      }
      */

      if (err.message?.includes("password")) {
        window.alert("Actualización fallida: La contraseña debe tener al menos 6 caracteres");
      } else if (err.message?.includes("email-already-in-use")) {
        window.alert("Actualización fallida: El usuario ya ha sido registrado");
      } else {
        window.alert("Actualización fallida: " + err.message);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  // Componente de carga inicial
  if (isLoading) {
    return (
      <div className="new">
        <Sidebar />
        <div className={containerClass}>
          <Navbar />
          <div className="loading-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 100px)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <CircularProgress />
              <p>Cargando datos del sistema...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="new">
      <Sidebar />
      <div className={containerClass}>
        <Navbar />
        <div className="top">
          <h1>{title}</h1>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleVolver}
            style={{ marginLeft: '20px' }}
          >
            Volver
          </Button>
        </div>
        <div className="bottom">
          <ImageUploader file={file} setFile={setFile} />
          <div className="right">
            {/* Indicador de carga para datos del usuario */}
            {isLoadingUserData && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                padding: '20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                <CircularProgress size={20} style={{ marginRight: '10px' }} />
                <span>Cargando datos del usuario...</span>
              </div>
            )}
            {/* Banner para usuarios que necesitan registro en sistema */}
            {needsAuth && (
              <div style={{ 
                backgroundColor: '#fff3cd', 
                color: '#856404', 
                padding: '10px', 
                marginBottom: '15px',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>Este usuario no está registrado en el sistema de autenticación</span>
                <Button 
                  variant="contained" 
                  color="warning" 
                  size="small"
                  onClick={() => setOpenAuthDialog(true)}
                >
                  Registrar ahora
                </Button>
              </div>
            )}

            {/* Banner para usuarios con email pero sin authUID */}
            {authStatus === 'needs_update' && (
              <div style={{ 
                backgroundColor: '#d1ecf1', 
                color: '#0c5460', 
                padding: '10px', 
                marginBottom: '15px',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>
                  {authMessage || 'Este usuario ya está registrado en el sistema, pero falta vincular su ID en el documento.'}
                </span>
                <Button 
                  variant="contained" 
                  color="info" 
                  size="small"
                  onClick={() => {
                    window.alert("Para corregir este problema, deberás actualizar el documento del usuario con su ID de autenticación. Contacta con el administrador del sistema para más información.");
                  }}
                >
                  Más información
                </Button>
              </div>
            )}
            
            <form onSubmit={handleAdd} style={{ opacity: isLoadingUserData ? 0.6 : 1, pointerEvents: isLoadingUserData ? 'none' : 'auto' }}>
              {/* Empresa arriba */}
              <div className="empresa-section">
                <div className="formInput">
                  <label>Empresa: <span className="required-indicator">*</span></label>
                  <EmpresaComponent
                    selectedEmpresa={selectedEmpresa}
                    setSelectedEmpresa={setSelectedEmpresa}
                    onEmpresaChange={handleEmpresaChange}
                  />
                </div>
              </div>

              {/* Fila de Clientes y Servicios lado a lado */}
              <div className="clientes-servicios-row">
                <div className="formInput">
                  <ClienteSearch
                    clientes={clientes}
                    selectedClientes={selectedClientes}
                    onChange={handleClienteChange}
                    disabled={isUpdating}
                    required={true}
                    searchTerm={clienteSearchTerm}
                    setSearchTerm={setClienteSearchTerm}
                  />
                </div>

                <div className="formInput">
                  <ServicioSearch
                    servicios={tiendasCliente}
                    selectedServicios={selectedOptions}
                    onChange={handleCheckboxChange}
                    disabled={isUpdating || tiendasCliente.length === 0}
                    required={true}
                    searchTerm={servicioSearchTerm}
                    setSearchTerm={setServicioSearchTerm}
                    showDisplayToggle={true}
                  />
                </div>
              </div>
              <div className="formInput">
                <label>Nombre:</label>
                <input
                  id="nombre"
                  type="text"
                  placeholder="Nombre"
                  onChange={handleInput}
                  value={data.nombre || ''}
                />
              </div>
              <div className="formInput">
                <label>Apellidos:</label>
                <input
                  id="apellidos"
                  type="text"
                  placeholder="Apellidos"
                  onChange={handleInput}
                  value={data.apellidos || ''}
                />
              </div>
              <div className="formInput">
                <label>Persona:</label>
                <input
                  id="persona"
                  type="number"
                  placeholder="Número de persona"
                  onChange={handleInput}
                  value={data.persona || ''}
                />
              </div>
              {/* Agrupa los campos de entrada en pares, excepto nombre y apellidos */}
              <div className="inputPairs">
                {inputs.map((input, index) => {
                  // Omitir el último campo sin etiqueta
                  if (!input.label) return null;
                  
                  return (
                    <div className="formInput" key={input.id}>
                      <label>
                        {input.label}
                        {input.id === 'email' && <span className="required-indicator">*</span>}
                      </label>
                      <input
                        id={input.id}
                        type={input.type}
                        placeholder={input.placeholder}
                        onChange={handleInput}
                        value={data[input.id] || ''}
                        required={input.id === 'email'}
                        disabled={input.id === "tipVS" || input.id === "dni"}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="button-container">
                <button 
                  disabled={per !== null && per < 100 || isUpdating} 
                  type="submit"
                >
                  {isUpdating ? "Actualizando..." : "Actualizar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Diálogo para confirmar registro en el sistema */}
      <Dialog
        open={openAuthDialog}
        onClose={() => setOpenAuthDialog(false)}
        aria-labelledby="auth-dialog-title"
      >
        <DialogTitle id="auth-dialog-title">
          Registrar usuario en el sistema de autenticación
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Desea registrar este usuario en el sistema? Esto le permitirá iniciar sesión en la aplicación.
          </DialogContentText>
          <DialogContentText style={{ marginTop: '10px', color: 'rgba(0, 0, 0, 0.6)' }}>
            {data.email ? (
              <span>Se utilizará el email: <strong>{data.email}</strong></span>
            ) : (
              <span>Se generará un email automáticamente para este usuario.</span>
            )}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="password"
            label="Contraseña (mínimo 6 caracteres)"
            type="password"
            fullWidth
            variant="outlined"
            value={authPassword}
            onChange={(e) => setAuthPassword(e.target.value)}
            placeholder={data.dni && data.dni.length >= 6 ? `Por defecto se usará: ${data.dni}` : ""}
            helperText={
              authPassword && authPassword.length < 6 
                ? "La contraseña debe tener al menos 6 caracteres" 
                : ""
            }
            error={authPassword && authPassword.length < 6}
            required={!data.dni || data.dni.length < 6}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAuthDialog(false)} color="primary">
            Cancelar
          </Button>
          <Button 
            onClick={handleRegisterAuth} 
            color="primary"
            disabled={isRegisteringAuth || (authPassword && authPassword.length < 6 && (!data.dni || data.dni.length < 6))}
          >
            {isRegisteringAuth ? "Registrando..." : "Registrar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mensaje de éxito */}
      <Snackbar 
        open={showSuccessMessage} 
        autoHideDuration={1500} 
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {updateMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default Update;